import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { FeaturedItem } from "@/mocks/featured";

interface HeroCarouselProps {
  items: FeaturedItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const goTo = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const active = items[index];

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden rounded-lg md:h-[520px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点轮播"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 焦点图`}
            title={`${item.title} 精选推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/15"></div>
        </div>
      ))}

      <div className="absolute inset-x-0 bottom-0 flex w-full flex-col p-5 md:p-8">
        <span className="mb-2 w-fit rounded-full bg-primary-500 px-3 py-1 text-[11px] font-bold tracking-wide text-background-50">
          本周焦点
        </span>
        <h2 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
          {active.title}
        </h2>
        <p className="mt-1 text-sm font-medium text-primary-200 md:text-base">{active.tagline}</p>
        <p className="mt-2 line-clamp-2 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">
          {active.intro}
        </p>
        <Link
          to={`/detail/${active.mediaId}`}
          className="mt-4 flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-100"
        >
          <i className="ri-play-circle-line text-lg"></i>
          查看详情
        </Link>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:left-4 md:h-10 md:w-10"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:right-4 md:h-10 md:w-10"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 right-5 flex items-center gap-1.5 md:right-8">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-primary-400" : "w-1.5 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}